import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, MessageCircle, Menu, X } from "lucide-react";
import { useI18n } from "../../i18n";
import { CONTACT } from "../../data";

const NavLink = ({ href, children, onClick }) => (
  <a
    href={href}
    onClick={onClick}
    data-testid={`nav-link-${href.replace("#", "")}`}
    className="link-underline font-body text-[13px] uppercase tracking-[0.18em] text-cream/80 hover:text-cream transition-colors"
  >
    {children}
  </a>
);

export default function Navbar() {
  const { t, lang, toggle } = useI18n();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const close = () => setOpen(false);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "backdrop-blur-xl bg-[#0b0b0a]/70 border-b border-white/5" : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 flex items-center justify-between h-[74px]">
        <a href="#home" data-testid="nav-logo" className="leading-none">
          <span className="font-script text-lime text-[30px] md:text-[34px] block -mb-1">Carpe Diem</span>
          <span className="font-body text-[9px] md:text-[10px] uppercase tracking-[0.4em] text-cream/60">
            Hair Style by Anna
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-9">
          <NavLink href="#work">{t.nav.work}</NavLink>
          <NavLink href="#services">{t.nav.services}</NavLink>
          <NavLink href="#about">{t.nav.about}</NavLink>
          <NavLink href="#contact">{t.nav.contact}</NavLink>
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={toggle}
            data-testid="lang-toggle"
            className="font-body text-[12px] uppercase tracking-[0.2em] text-cream/70 hover:text-lime transition-colors border border-white/15 rounded-full px-3 py-1.5"
          >
            {lang === "it" ? "EN" : "IT"}
          </button>
          <a
            href={CONTACT.phoneHref}
            data-testid="nav-call-btn"
            className="hidden sm:flex items-center gap-2 text-cream/80 hover:text-lime transition-colors"
            aria-label="call"
          >
            <Phone size={17} />
          </a>
          <a
            href={CONTACT.whatsappHref}
            target="_blank"
            rel="noreferrer"
            data-testid="nav-whatsapp-btn"
            className="hidden sm:inline-flex items-center gap-2 bg-lime text-[#0b0b0a] font-body font-semibold text-[13px] px-4 py-2 rounded-full hover:bg-[#b6ff3c] transition-colors"
          >
            <MessageCircle size={15} /> {t.cta.whatsapp}
          </a>
          <button
            onClick={() => setOpen((o) => !o)}
            data-testid="mobile-menu-toggle"
            className="lg:hidden text-cream"
            aria-label="menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-[#0b0b0a]/95 backdrop-blur-xl border-t border-white/5"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-8 flex flex-col gap-6">
              <NavLink href="#work" onClick={close}>{t.nav.work}</NavLink>
              <NavLink href="#services" onClick={close}>{t.nav.services}</NavLink>
              <NavLink href="#about" onClick={close}>{t.nav.about}</NavLink>
              <NavLink href="#contact" onClick={close}>{t.nav.contact}</NavLink>
              <div className="flex gap-3 pt-2">
                <a href={CONTACT.phoneHref} data-testid="mobile-call-btn" className="flex-1 text-center border border-white/15 rounded-full py-3 text-cream font-body text-sm">
                  {t.cta.call}
                </a>
                <a href={CONTACT.whatsappHref} target="_blank" rel="noreferrer" data-testid="mobile-whatsapp-btn" className="flex-1 text-center bg-lime text-[#0b0b0a] rounded-full py-3 font-body font-semibold text-sm">
                  {t.cta.whatsapp}
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
